/**
 * @description Component-ScaleLimitPreviewContent
 */
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useWrappedOrderList } from '@/features/account/orderHook';
import { useCurrentPairByDevice } from '@/features/pair/hook';
import { useSimulateBatchPlaceScaledLimitOrder } from '@/features/trade/query';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import _ from 'lodash';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ScaleLimitPreviewRow from './ScaleLimitPreviewRow';

interface IPropTypes {}

/**
 * ScaleLimitPreviewContent component for displaying the table content of scale limit preview
 * @param simulatedOrders - Array of simulated orders
 * @param openOrders - Current open orders
 * @param currentPair - Current trading pair
 * @param tokenPriceInfo - Token price information
 */
const ScaleLimitPreviewContent: FC<IPropTypes> = function () {
  const { t } = useTranslation();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { data: simulation } = useSimulateBatchPlaceScaledLimitOrder(chainId);
  const simulatedOrders = useMemo(
    () =>
      _.orderBy(simulation?.data?.orders, [(order) => WrappedBigNumber.from(order.limitPrice).stringValue], ['desc']),
    [simulation?.data?.orders],
  );
  const currentPair = useCurrentPairByDevice(chainId);
  const openOrders = useWrappedOrderList(chainId, userAddr, currentPair?.id);

  if (!simulatedOrders?.length) return null;

  return (
    <div className="syn-scale-limit-preview-table">
      <div className="syn-scale-limit-preview-table-header">
        <span>{t('common.price')}</span>
        <span>{t('common.size')}</span>
        <span>{t('common.margin')}</span>
      </div>
      {simulatedOrders?.map((order) => (
        <ScaleLimitPreviewRow key={order.tick} orderList={openOrders} currentPair={currentPair} order={order} />
      ))}
    </div>
  );
};

export default ScaleLimitPreviewContent;
